<template>
  <div class="domain-suspension">
    <div class="domain-suspension__actions d-flex">
      <v-btn @click="cancel" class="ml-auto cancel__btn" color="primary" outlined>
        {{ $t("generic.buttons.cancel") }}
      </v-btn>
      <v-btn @click="save" :loading="saving" color="primary">
        {{ $t("generic.buttons.save") }}
      </v-btn>
    </div>

    <!-- Suspended toggle -->
    <v-card class="domain-suspension__card spacing-bottom--24">
      <div class="d-flex align-center">
        <div>
          <div class="domain-suspension__card-title">{{ $t("resources.domains.suspension.suspended_title") }}</div>
          <div class="domain-suspension__card-subtitle">
            {{ $t("resources.domains.suspension.suspended_subtitle") }}
          </div>
        </div>
        <div class="ml-auto d-flex align-center">
          <span class="domain-suspension__toggle-label">{{ suspended ? $t("generic.yes") : $t("generic.no") }}</span>
          <v-switch
            class="ml-2 domain-suspension__switch"
            v-model="suspended"
            inset
            color="primary"
            hide-details
            :ripple="false"></v-switch>
        </div>
      </div>
    </v-card>

    <!-- Add / current suspension -->
    <v-card v-if="suspended" class="domain-suspension__card spacing-bottom--24">
      <div class="domain-suspension__card-heading">
        {{
          initialSuspended
            ? $t("resources.domains.suspension.current_title")
            : $t("resources.domains.suspension.add_title")
        }}
      </div>
      <VaDatePicker
        class="domain-suspension__date spacing-bottom--16"
        :label="$t('resources.domains.suspension.date_label')"
        v-model="form.suspendedAt"
        :disabled="initialSuspended"
        hide-details></VaDatePicker>
      <v-label>{{ $t("resources.domains.suspension.reason_label") }}</v-label>
      <v-select
        class="domain-suspension__reason"
        v-model="form.reason"
        :items="reasons"
        item-text="text"
        item-value="value"
        append-icon="mdi-chevron-down"
        outlined
        hide-details="auto"
        :disabled="initialSuspended"
        :placeholder="$t('resources.domains.suspension.reason_placeholder')"
        :error-messages="reasonError ? $t('resources.domains.suspension.reason_required') : ''"
        @change="reasonError = false"></v-select>
    </v-card>

    <!-- Suspension history -->
    <v-card class="domain-suspension__card">
      <div class="domain-suspension__card-heading">{{ $t("resources.domains.suspension.history_title") }}</div>
      <va-list
        ref="list"
        resource="domain_suspensions"
        disable-query-string
        disable-export
        disable-create
        hide-domain-filter
        :ignore-domain-filter="true"
        :filter.sync="historyFilter"
        :sort-by="['suspendedAt']"
        :sort-desc="[true]"
        :items-per-page="10">
        <template #no-data>
          <div class="no-results">{{ $t("custom.no_data") }}</div>
        </template>
        <template #no-results>
          <div class="no-results">{{ $t("custom.no_data") }}</div>
        </template>
        <va-data-table disable-select :disable-clone="true" :fields="historyFields">
          <template #[`field.suspendedAt`]="{ item }">
            {{ changeDateFormat(item.suspendedAt, isUserUS($store.state.user.timezone)) }}
          </template>
          <template #[`field.reason`]="{ item }">
            {{ reasonLabel(item.reason) }}
          </template>
          <template #[`field.createdBy`]="{ item }">
            <div class="d-flex align-center gap-1">
              <span>{{ createdByName(item.createdBy) }}</span>
              <span v-if="isCurrentUser(item.createdBy)" class="domain-suspension__you-badge">
                {{ $t("generic.you") }}
              </span>
            </div>
          </template>
          <template #[`cell.actions`]="{ item }">
            <DataListActions :actions="rowActions(item)"></DataListActions>
          </template>
        </va-data-table>
      </va-list>
    </v-card>

    <!-- Edit suspension dialog -->
    <Modal
      v-model="editDialog"
      :title="$t('resources.domains.suspension.edit_title')"
      :cta-text="$t('generic.buttons.save')"
      :cta-loading="editSaving"
      :cta-disabled="!editForm.reason"
      @confirm="saveEdit"
      @cancel="editDialog = false">
      <VaDatePicker
        class="domain-suspension__date spacing-bottom--16"
        :label="$t('resources.domains.suspension.date_label')"
        v-model="editForm.suspendedAt"
        hide-details></VaDatePicker>
      <v-label>{{ $t("resources.domains.suspension.reason_label") }}</v-label>
      <v-select
        class="domain-suspension__reason"
        v-model="editForm.reason"
        :items="reasons"
        item-text="text"
        item-value="value"
        append-icon="mdi-chevron-down"
        outlined
        hide-details
        :placeholder="$t('resources.domains.suspension.reason_placeholder')"></v-select>
    </Modal>

    <!-- Delete suspension confirmation -->
    <Modal
      v-model="deleteDialog"
      width="400"
      dialog-icon="danger"
      :title="$t('resources.domains.suspension.delete_title')"
      :subtitle="$t('resources.domains.suspension.delete_subtitle')"
      :cta-text="$t('generic.buttons.delete')"
      cta-color="error"
      :cta-loading="deleting"
      @confirm="confirmDelete"
      @cancel="deleteDialog = false"></Modal>
  </div>
</template>

<script>
  import { format } from "date-fns";
  import { mapGetters } from "vuex";
  import { changeDateFormat } from "@/@core/utils/filter";
  import { isUserUS } from "@/utility/isUserUS";
  import { formErrorMessage } from "@/utility/formErrorMessage";
  import { DOMAIN_SUSPENSION_REASONS } from "@/constant";
  import VaDatePicker from "@/components/custom/VaDatePicker";
  import Modal from "@/components/custom/Modal.vue";
  import DataListActions from "@/components/custom/DataListActions.vue";

  export default {
    name: "DomainSuspension",
    components: {
      VaDatePicker,
      Modal,
      DataListActions,
    },
    props: {
      domain: {
        type: Object,
        default: () => ({}),
      },
    },
    data() {
      return {
        changeDateFormat,
        isUserUS,
        reasons: DOMAIN_SUSPENSION_REASONS,
        suspended: !!this.domain.suspended,
        initialSuspended: !!this.domain.suspended,
        saving: false,
        reasonError: false,
        form: {
          reason: null,
          suspendedAt: format(new Date(), "yyyy-MM-dd"),
        },
        historyFilter: { domain: [this.domain.id] },
        historyFields: [
          {
            source: "suspendedAt",
            label: this.$t("resources.domains.suspension.date_label"),
            sortable: true,
            width: "20%",
          },
          {
            source: "reason",
            label: this.$t("resources.domains.suspension.reason_label"),
            sortable: true,
            width: "50%",
          },
          {
            source: "createdBy",
            label: this.$t("resources.domains.suspension.history_created_by"),
            sortable: false,
            width: "30%",
          },
        ],
        editDialog: false,
        editSaving: false,
        editForm: {
          id: null,
          reason: null,
          suspendedAt: null,
        },
        deleteDialog: false,
        deleting: false,
        deleteId: null,
      };
    },
    computed: {
      ...mapGetters(["getCurrentUser"]),
      currentSuspension() {
        if (!this.domain.suspensions?.length) return null;

        return [...this.domain.suspensions].sort((a, b) => new Date(b.suspendedAt) - new Date(a.suspendedAt))[0];
      },
    },
    created() {
      this.resetForm();
    },
    watch: {
      "domain.suspended"(val) {
        this.suspended = !!val;
        this.initialSuspended = !!val;
        this.resetForm();
      },
    },
    methods: {
      resetForm() {
        if (this.initialSuspended && this.currentSuspension) {
          this.form.reason = this.currentSuspension.reason;
          this.form.suspendedAt = format(new Date(this.currentSuspension.suspendedAt), "yyyy-MM-dd");
        } else {
          this.form.reason = null;
          this.form.suspendedAt = format(new Date(), "yyyy-MM-dd");
        }
        this.reasonError = false;
      },
      reasonLabel(value) {
        return this.reasons.find(reason => reason.value === value)?.text || value;
      },
      createdByName(createdBy) {
        if (!createdBy) return "-";

        return `${createdBy.firstName || ""} ${createdBy.lastName || ""}`.trim() || "-";
      },
      isCurrentUser(createdBy) {
        if (!createdBy) return false;

        return createdBy.id === this.getCurrentUser.id;
      },
      async save() {
        if (this.suspended === this.initialSuspended) return;

        if (this.suspended) {
          if (!this.form.reason) {
            this.reasonError = true;

            return;
          }

          await this.mutateSuspension("POST", `domains/${this.domain.id}/suspend`, {
            reason: this.form.reason,
            suspendedAt: this.form.suspendedAt,
          });
        } else {
          await this.mutateSuspension("POST", `domains/${this.domain.id}/unsuspend`);
        }
      },
      async mutateSuspension(method, url, params) {
        this.saving = true;

        try {
          await this.$admin.dataProvider.customHttpCall(method, url, params);
        } catch (e) {
          this.suspended = this.initialSuspended;
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
          this.saving = false;

          return;
        }

        this.$admin.toast.success(
          this.suspended
            ? this.$t("resources.domains.suspension.suspend_success")
            : this.$t("resources.domains.suspension.unsuspend_success"),
        );

        try {
          await this.refreshAfterMutation();
        } finally {
          this.saving = false;
        }
      },
      async refreshAfterMutation() {
        this.$store.dispatch("api/refresh", "domain_suspensions");

        const { data } = await this.$store.dispatch("domains/getOne", { id: this.domain.id });
        this.$store.commit("domains/setItem", data);

        this.initialSuspended = !!data.suspended;
        this.suspended = !!data.suspended;
        this.resetForm();
      },
      isActiveSuspension(item) {
        return this.initialSuspended && this.currentSuspension?.id === item.id;
      },
      rowActions(item) {
        const actions = [
          {
            title: this.$t("generic.buttons.edit"),
            icon: "mdi-pencil-outline",
            function: this.openEditDialog.bind(this, item),
          },
        ];

        if (!this.isActiveSuspension(item)) {
          actions.push({
            title: this.$t("generic.buttons.delete"),
            icon: "mdi-trash-can-outline",
            function: this.openDeleteDialog.bind(this, item),
          });
        }

        return actions;
      },
      openEditDialog(item) {
        this.editForm = {
          id: item.id,
          reason: item.reason,
          suspendedAt: format(new Date(item.suspendedAt), "yyyy-MM-dd"),
        };
        this.editDialog = true;
      },
      async saveEdit() {
        this.editSaving = true;
        try {
          await this.$admin.dataProvider.customHttpCall("PATCH", `domain_suspensions/${this.editForm.id}`, {
            reason: this.editForm.reason,
            suspendedAt: this.editForm.suspendedAt,
          });
          this.$store.dispatch("api/refresh", "domain_suspensions");
          this.editDialog = false;
          this.$admin.toast.success(this.$t("resources.domains.suspension.update_success"));
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.editSaving = false;
        }
      },
      openDeleteDialog(item) {
        this.deleteId = item.id;
        this.deleteDialog = true;
      },
      async confirmDelete() {
        this.deleting = true;

        try {
          await this.$admin.dataProvider.customHttpCall("DELETE", `domain_suspensions/${this.deleteId}`);
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
          this.deleting = false;

          return;
        }

        this.deleteDialog = false;
        this.deleteId = null;
        this.$admin.toast.success(this.$t("resources.domains.suspension.delete_success"));

        try {
          await this.refreshAfterMutation();
        } finally {
          this.deleting = false;
        }
      },
      cancel() {
        this.suspended = this.initialSuspended;
        this.resetForm();
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../../@core/preset/preset/functions.scss";
  @import "../../../@core/preset/preset/typography/index.scss";

  .domain-suspension {
    &__actions {
      margin: 26px 0px;
      button {
        &:first-child {
          margin-right: 16px;
        }
      }
    }

    &__card {
      padding: 16px;
    }

    &__card-title {
      @include heading-4();
      color: get-color("headline");
      margin-bottom: 6px;
    }

    &__card-subtitle {
      @include text-subtitle-2();
      color: get-color("subtitle");
    }

    &__card-heading {
      @include heading-4();
      color: get-color("headline");
      margin-bottom: 16px;
    }

    &__toggle-label {
      @include text-body-2();
      color: get-color("body");
    }

    &__you-badge {
      @include text-caption();
      color: get-color("subtitle");
      margin-left: 8px;
      padding: 2px 8px;
      border-radius: 4px;
      border: 1px solid get-color("border");
    }
  }

  .v-label {
    color: get-color("subtitle") !important;
    @include text-caption-bold();
  }

  .no-results {
    padding: 24px 0;
    text-align: center;
    color: get-color("subtitle");
  }
</style>
